import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/api';
import { Button, Input, Card } from '../../components/ui/Kit';
import Spinner from '../../components/ui/Spinner';

const fullPageSpinner = (
  <div className="min-h-screen w-full flex flex-col items-center justify-center bg-gray-50 text-gray-600 gap-3">
    <Spinner size="lg" className="text-primary h-8 w-8" />
    <p className="text-sm">Verifying your invitation…</p>
  </div>
);

export default function AcceptInvite() {
  const navigate = useNavigate();

  const [resolved, setResolved] = useState(false);
  const [session, setSession] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!supabase) {
      setResolved(true);
      return undefined;
    }

    let finished = false;
    let cancelled = false;

    const finish = (nextSession = null) => {
      if (finished || cancelled) return;
      finished = true;
      if (nextSession?.user) setSession(nextSession);
      setResolved(true);
    };

    // Let detectSessionInUrl establish the invite session; do not re-exchange tokens.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (cancelled || finished) return;
      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && nextSession?.user) {
        finish(nextSession);
      }
    });

    const bootstrap = async () => {
      try {
        const {
          data: { session: existing },
        } = await supabase.auth.getSession();
        if (cancelled || finished) return;
        if (existing?.user) {
          finish(existing);
          return;
        }
      } catch {
        if (!cancelled && !finished) finish(null);
      }
    };

    void bootstrap();
    const timeout = window.setTimeout(() => finish(null), 8000);

    return () => {
      cancelled = true;
      subscription.unsubscribe();
      window.clearTimeout(timeout);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setSubmitting(true);
    try {
      const { error: pwErr } = await supabase.auth.updateUser({ password });
      if (pwErr) throw pwErr;
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  if (!resolved) {
    return fullPageSpinner;
  }

  if (!supabase) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <Card className="max-w-md w-full p-8 shadow-xl border-0 ring-1 ring-gray-200">
          <p className="text-gray-700 text-sm">
            Accepting invitations requires Supabase configuration (
            <code className="text-xs bg-gray-100 px-1 rounded">VITE_SUPABASE_URL</code>
            {' / '}
            <code className="text-xs bg-gray-100 px-1 rounded">VITE_SUPABASE_ANON_KEY</code>
            ).
          </p>
          <Link to="/login" className="mt-4 inline-block text-sm font-medium text-primary hover:text-primary-dark">
            Back to sign in
          </Link>
        </Card>
      </div>
    );
  }

  if (!session?.user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <Card className="max-w-md w-full p-8 shadow-xl border-0 ring-1 ring-gray-200 text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Invalid or expired link</h2>
          <p className="text-sm text-gray-600 mb-6">
            Open the invitation from your email again, or ask your admin to resend it.
          </p>
          <Button type="button" variant="outline" className="w-full" onClick={() => navigate('/login')}>
            Sign in
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="max-w-md w-full p-8 shadow-xl border-0 ring-1 ring-gray-200">
        <div className="text-center mb-8">
          <div className="mx-auto h-12 w-12 bg-primary rounded-lg shadow-sm flex items-center justify-center mb-4">
            <span className="text-white font-bold text-2xl">P</span>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Set your password</h2>
          <p className="mt-2 text-sm text-gray-600">Choose a password to finish joining your team.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-md border border-red-100">{error}</div>
          )}

          <Input
            label="Password"
            type="password"
            required
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
          />

          <Input
            label="Confirm password"
            type="password"
            required
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="••••••••"
          />

          <Button type="submit" className="w-full" isLoading={submitting} size="lg">
            Continue to dashboard
          </Button>
        </form>
      </Card>
    </div>
  );
}
